import React, { useEffect, useState } from 'react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as api from '../../lib/api';
import { getIcon } from '../../lib/constants';
import { CHART_HUE, chartGridProps, chartAxisProps, chartLineProps, chartAreaGradient, lastPointDot, ChartTooltip } from '../../theme/chartTheme';
import { toast } from 'sonner';
import { SectionHeader, Segmented, Stat, StatGrid, Loading, fmtDateTime, fmtMoney } from './ui';
import { friendlyError } from '../../lib/errorReporting';

// Resumen — one dominant number (total users) with this week's growth, then
// quiet label/value stats grouped by topic and separated by space (no card
// per number), minimal charts, and recent activity in the app's list style.

function DayChart({ title, data }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
      <h3 className="text-sm font-medium text-slate-300 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid {...chartGridProps} />
          <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={24} {...chartAxisProps} />
          <YAxis allowDecimals={false} width={28} {...chartAxisProps} />
          <Tooltip cursor={{ fill: 'rgba(255,255,255,0.03)' }} content={<ChartTooltip />} />
          <Bar dataKey="count" name="Total" fill={CHART_HUE} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Errors in the last 24 h by origin, and kinds never seen before (each of
// those also emails the admins). Details in Logs → Errores.
function ErrorsSummary() {
  const [data, setData] = useState(null);
  useEffect(() => { api.getAdminErrorsSummary().then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const s = data.by_source || {};
  return (
    <section data-testid="admin-errors-summary">
      <SectionHeader title="Errores" subtitle="Últimas 24 horas · detalle en Logs → Errores" />
      <StatGrid>
        <Stat label="Total" value={data.total_24h} sub={data.new_kinds_24h ? `${data.new_kinds_24h} tipo(s) nuevo(s)` : 'Ningún tipo nuevo'} />
        <Stat label="Servidor" value={s.server || 0} />
        <Stat label="App" value={s.app || 0} />
        <Stat label="Integraciones y tareas" value={(s.integration || 0) + (s.job || 0)} sub={`${s.validation || 0} de validación`} />
      </StatGrid>
    </section>
  );
}

// Push and Gmail as they really are, not just "has a row" (admin.py
// /admin/delivery-health). Push is confirmed by each phone on app load;
// Gmail by refreshing every connection's token with "Verificar ahora".
function DeliveryHealth() {
  const [data, setData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const load = () => api.getAdminDeliveryHealth().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await api.verifyAdminGmail();
      toast.success(`Gmail: ${r.connected} funcionando, ${r.reauth_required} necesitan reconectar${r.error ? `, ${r.error} con error` : ''}`);
      load();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo verificar'));
    } finally {
      setVerifying(false);
    }
  };

  if (!data) return null;
  const { push, gmail } = data;
  return (
    <section data-testid="admin-delivery-health">
      <SectionHeader
        title="Push y Gmail reales"
        subtitle="Lo que de verdad funciona, no solo lo guardado"
        right={
          <button onClick={verify} disabled={verifying}
            className="h-8 px-3 rounded-full text-xs font-medium bg-white/[0.06] text-violet-300 hover:bg-white/10 transition-colors whitespace-nowrap disabled:opacity-50">
            {verifying ? 'Verificando Gmail…' : 'Verificar Gmail ahora'}
          </button>
        }
      />
      <StatGrid>
        <Stat label="Push confirmado por el teléfono" value={push.confirmed}
          sub={`${push.with_row} con suscripción guardada · ${push.contradicted} el teléfono dice que no · ${push.unreported} sin reportar aún`} />
        <Stat label="Push entregado (14 días)" value={push.delivered_14d} />
        <Stat label="Gmail funcionando" value={gmail.connected}
          sub={gmail.verified ? `Verificado ${fmtDateTime(gmail.last_verified_at)}` : 'Sin verificar todavía'} />
        <Stat label="Gmail por reconectar" value={gmail.reauth_required} sub={`${gmail.total} conexiones guardadas`} />
      </StatGrid>
    </section>
  );
}

// Last 30 days as a continuous series — the API only returns days that had
// signups, and skipping the empty ones would make the curve lie.
function last30Days(byDay) {
  const counts = Object.fromEntries((byDay || []).map(d => [d.date, d.count]));
  const out = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({
      date: key,
      label: d.toLocaleDateString('es-SV', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
      count: counts[key] || 0,
    });
  }
  return out;
}

// Same look as Análisis → "Balance en el tiempo": area with a soft gradient,
// violet line, a dot only on the latest point.
function UsersChart({ byDay, totalUsers }) {
  const [mode, setMode] = useState('new');
  const days = last30Days(byDay);
  let running = totalUsers - days.reduce((s, d) => s + d.count, 0);
  const data = days.map(d => {
    running += d.count;
    return { ...d, value: mode === 'new' ? d.count : running };
  });
  return (
    <section data-testid="admin-users-chart">
      <SectionHeader
        title={mode === 'new' ? 'Usuarios nuevos' : 'Usuarios totales'}
        subtitle="Últimos 30 días"
        right={<Segmented options={[{ key: 'new', label: 'Nuevos por día' }, { key: 'total', label: 'Total acumulado' }]} value={mode} onChange={setMode} />}
      />
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            {chartAreaGradient('usersGrad')}
            <CartesianGrid {...chartGridProps} />
            <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={24} {...chartAxisProps} />
            <YAxis allowDecimals={false} width={36} domain={mode === 'total' ? ['dataMin', 'auto'] : [0, 'auto']} {...chartAxisProps} />
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              name={mode === 'new' ? 'Nuevos' : 'Total'}
              fill="url(#usersGrad)"
              dot={lastPointDot(data.length)}
              isAnimationActive={false}
              {...chartLineProps}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [overview, txns] = await Promise.all([
          api.getAdminOverview(),
          api.getAdminTransactions({ page: 1, limit: 10 }),
        ]);
        setData(overview);
        setRecentTxns(txns.items);
      } catch (e) {
        setError('No se pudo cargar el resumen.');
      }
    })();
  }, []);

  if (error) return <p className="text-rose-400 text-sm">{error}</p>;
  if (!data) return <Loading />;

  const { users, transactions: tx, revenue, referrals, goals } = data;
  const src = tx.by_source;

  return (
    <div className="space-y-10 pb-10" data-testid="admin-overview">
      {/* Hero */}
      <div>
        <p className="text-[13px] font-medium text-slate-500">Usuarios</p>
        <p className="mt-1 text-5xl sm:text-6xl font-semibold leading-none tracking-[-0.03em] text-white tabular-nums">{users.total}</p>
        <p className="mt-3 text-sm text-slate-400">
          <span className="text-emerald-400 font-medium">+{users.signups_7d}</span> esta semana
          <span className="text-slate-600"> · </span>+{users.signups_30d} en 30 días
        </p>
      </div>

      <UsersChart byDay={data.signups_by_day} totalUsers={users.total} />

      <ErrorsSummary />

      <DeliveryHealth />

      <section>
        <SectionHeader title="Actividad" subtitle="Usuarios que abrieron la app" />
        <StatGrid>
          <Stat label="Hoy" value={users.dau} />
          <Stat label="Esta semana" value={users.wau} />
          <Stat label="Este mes" value={users.mau} />
          <Stat label="Transacciones hoy" value={tx.today} sub={`${tx.last_7d} en 7 días · ${tx.total} en total`} />
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Planes e ingresos" />
        <StatGrid>
          <Stat label="En trial" value={users.active_trial} sub="Nunca pagaron, dentro del trial" dot="#38bdf8" />
          <Stat label="Pro pagado" value={users.active_pro} sub="Con plan pagado activo" dot="#a78bfa" />
          <Stat label="Ingresos" value={fmtMoney(revenue.total_paid)} sub={`${revenue.paid_count} pagos`} dot="#34d399" />
          <Stat label="Popup de urgencia" value={data.trial_urgency_acknowledged} sub="Ya respondieron (actualizar o seguir)" />
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Cómo registran" subtitle="Transacciones por método de entrada" />
        <StatGrid>
          <Stat label="Manual" value={src.manual} sub="Incluye voz antes del 17 sep" />
          <Stat label="Voz" value={src.voice} sub="Desde el 17 sep" />
          <Stat label="Gmail" value={src.email} sub="Detección automática" />
          <Stat label="Recibo (foto)" value={src.ocr ?? 0} sub="Escaneo de recibos" />
          <Stat label="Coach" value={src.chat ?? 0} sub="Anotado por chat" />
        </StatGrid>
      </section>

      <section>
        <SectionHeader title="Metas y referidos" />
        <StatGrid>
          <Stat label="Metas" value={goals?.total ?? 0} sub={`${goals?.shared ?? 0} compartidas · ${goals?.shared_members ?? 0} participantes`} dot="#8b5cf6" />
          <Stat label="Aportes" value={goals?.contributions ?? 0} sub={`${fmtMoney(goals?.contributed_amount)} ahorrado`} />
          <Stat label="Registros por invitación" value={referrals.signups} sub="Con link de referido" />
          <Stat label="Referidos recompensados" value={referrals.rewarded} sub="Hicieron su primer registro" />
        </StatGrid>
      </section>

      {/* Signups now live in the area chart up top. */}
      <DayChart title="Transacciones por día (30 días)" data={last30Days(data.transactions_by_day)} />

      <section>
        <SectionHeader title="Actividad reciente" subtitle="Últimos movimientos de todas las cuentas" />
        <div className="space-y-1">
          {recentTxns.map((t) => {
            const Icon = getIcon(t.category_icon);
            const isIncome = t.type === 'income';
            return (
              <div key={t.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: (t.category_color || '#94a3b8') + '15' }}>
                    <Icon className="w-4 h-4" style={{ color: t.category_color || '#94a3b8' }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">
                      {t.description || t.category_name || t.type}
                      {t.type === 'goal' && <span className="text-slate-500 font-normal"> · aporte a meta</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {t.user_email || t.user_id}{t.user_name ? ` · ${t.user_name}` : ''} · {fmtDateTime(t.date)}
                    </p>
                  </div>
                </div>
                <span className={`shrink-0 text-sm font-semibold tabular-nums ${isIncome ? 'text-emerald-400' : 'text-slate-200'}`}>
                  {isIncome ? '+' : '-'}{fmtMoney(t.amount)}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
